import { visibleCampaignCategories, type CampaignCategory } from '@/lib/campaigns/campaignCategories';
import { describeMinViewsRequirement, type MinViewsRequirement } from '@/lib/campaigns/describeMinViewsRequirement';
import { isProExclusiveCampaign } from '@/lib/campaigns/isProExclusiveCampaign';
import { resolveCampaignAvatar } from '@/lib/campaigns/resolveCampaignAvatar';
import { parseRequiredHashtags } from '@/lib/parseRequiredHashtags';
import type { CampaignCompetitionFields } from './rankingCompetition';

export type CampaignPlatform = 'tiktok' | 'instagram' | 'youtube';

export type CommunityCampaignRow = CampaignCompetitionFields & {
  id: string;
  name: string;
  logo_url?: string | null;
  banner_url?: string | null;
  categories?: ReadonlyArray<string | null> | null;
  required_hashtags?: string | null;
  invite_only?: boolean | null;
  selected_only?: boolean | null;
  is_selected_user?: boolean | null;
  my_approved_platforms?: ReadonlyArray<string> | null;
  cpm_rate_cents?: number | null;
  min_views_threshold?: number | null;
  currency?: string | null;
  budget_total_cents?: number | null;
  budget_remaining_cents?: number | null;
  budget_pending_cents?: number | null;
  youtube_channels?: ReadonlyArray<{ thumbnail?: string | null } | null> | null;
};

export type CommunityCampaignCard = {
  campaignType?: string;
  rankingConfig?: unknown;
  id: string;
  name: string;
  bannerUrl: string | null;
  avatarSrc: string | null;
  initials: string;
  categories: CampaignCategory[];
  isLocked: boolean;
  isSelectedMode: boolean;
  isProExclusive: boolean;
  cpmRateCents: number;
  currency: string;
  minViews: MinViewsRequirement;
  budgetTotalCents: number;
  budgetRemainingCents: number;
  budgetPendingCents: number;
  /** Budget already paid out (total − remaining), never negative. */
  budgetSpentCents: number;
  /** budgetSpentCents as a whole percentage of the total, 0–100. */
  budgetUsedPercent: number;
  /** Platforms the campaign pays on, from its per-platform hashtags. */
  activePlatforms: CampaignPlatform[];
};

export function toCommunityCampaignCard(
  row: CommunityCampaignRow,
  { isPaidUser }: { isPaidUser: boolean },
): CommunityCampaignCard {
  const avatar = resolveCampaignAvatar(row);
  const isSelectedMode = !!row.selected_only;
  const hashtags = parseRequiredHashtags(row.required_hashtags ?? null);
  const budgetTotalCents = row.budget_total_cents || 0;
  const budgetSpentCents = Math.max(0, budgetTotalCents - (row.budget_remaining_cents || 0));
  const budgetUsedPercent = budgetTotalCents > 0
    ? Math.min(100, Math.round((budgetSpentCents / budgetTotalCents) * 100))
    : 0;
  const activePlatforms = (['tiktok', 'instagram', 'youtube'] as const).filter((p) => !!hashtags[p]);
  return {
    campaignType: row.campaign_type || 'cpm',
    rankingConfig: row.ranking_config,
    id: row.id,
    name: row.name,
    bannerUrl: row.banner_url?.trim() ? row.banner_url : null,
    avatarSrc: avatar.src,
    initials: avatar.initials,
    categories: visibleCampaignCategories(row.categories),
    isSelectedMode,
    // Selected-only campaigns lock on list membership alone — a paid plan and
    // an approved channel are both irrelevant there (same rule the channel
    // chips used before this row existed).
    isLocked: isSelectedMode
      ? !row.is_selected_user
      : !!row.invite_only && (row.my_approved_platforms?.length ?? 0) === 0 && !isPaidUser,
    isProExclusive: isProExclusiveCampaign({ inviteOnly: !!row.invite_only, isPaidUser }),
    cpmRateCents: row.cpm_rate_cents || 0,
    currency: row.currency || 'BRL',
    minViews: describeMinViewsRequirement(row.min_views_threshold),
    budgetTotalCents,
    budgetRemainingCents: row.budget_remaining_cents || 0,
    budgetPendingCents: row.budget_pending_cents || 0,
    budgetSpentCents,
    budgetUsedPercent,
    activePlatforms: [...activePlatforms],
  };
}

/** Accessible first, then locked; within each, higher CPM rate first; ties by name. */
export function sortCommunityCampaigns(cards: CommunityCampaignCard[]): CommunityCampaignCard[] {
  return [...cards].sort((a, b) => {
    if (a.isLocked !== b.isLocked) return a.isLocked ? 1 : -1;
    if (a.cpmRateCents !== b.cpmRateCents) return b.cpmRateCents - a.cpmRateCents;
    return a.name.localeCompare(b.name);
  });
}

export function buildCommunityCampaignCards(
  rows: ReadonlyArray<CommunityCampaignRow>,
  opts: { isPaidUser: boolean },
): CommunityCampaignCard[] {
  return sortCommunityCampaigns(rows.map((row) => toCommunityCampaignCard(row, opts)));
}
